import http from "@/http-common"
import type {
  ICreateEstablishment,
  IEstablishmentContact,
  IEstablishmentGeneral,
  IEstablishmentHours,
  IEstablishmentSpecific,
} from "@/types/Establishment.types"

class EstablishmentDataService {
  /**
   * Get a magic link for a user to access the subscription page with establishment context
   */
  getSubscriptionLinkForUser(establishmentId: number, userId: number): Promise<{ data: { data: { url: string } } }> {
    return http.get(`auth/establishments/${establishmentId}/users/${userId}/subscription-link`)
  }

  /**
   * Lien magique prospect vers le démonstrateur (calendrier éditorial) avec contexte établissement
   */
  getDemonstratorLinkForUser(establishmentId: number, userId: number): Promise<{ data: { data: { url: string } } }> {
    return http.get(`auth/establishments/${establishmentId}/users/${userId}/demonstrator-link`)
  }

  /**
   * Invite a prospect to an establishment
   */
  inviteProspect(establishmentId: number, data: {
    email: string
    first_name?: string
    last_name?: string
    phone?: string
  }): Promise<any> {
    return http.post(`auth/establishments/${establishmentId}/invite-prospect`, data)
  }

  /**
   * Get establishment details for sales view
   * @param establishmentId
   * @returns
   */
  getSalesEstablishmentDetails(establishmentId: number): Promise<any> {
    return http.get(`auth/establishments/${establishmentId}/sales-details`)
  }

  /**
   * Annule la facture Stripe (void) pour un contrat en virement (commercial non partner).
   */
  voidWireTransferContractInvoice(establishmentId: number, contractId: number): Promise<any> {
    return http.post(`auth/establishments/${establishmentId}/contracts/${contractId}/void-wire-invoice`)
  }

  /**
   * Update account payment settings (virement, SEPA 3x, facturation externe) for sales view
   * @param establishmentId
   * @param data
   * @returns
   */
  updateAccountPaymentSettings(
    establishmentId: number,
    data: {
      sale_user_id?: number | null
      allow_wire_transfer: boolean
      payment_facilities: boolean
      external_billing: boolean
    }
  ): Promise<any> {
    return http.put(`auth/establishments/${establishmentId}/account`, data)
  }

  /**
   * Update establishment sale comment for sales view
   * @param establishmentId
   * @param data
   * @returns
   */
  updateEstablishmentSaleComment(
    establishmentId: number,
    data: { sale_comment: string | null }
  ): Promise<any> {
    return http.put(`auth/establishments/${establishmentId}/sale-comment`, data)
  }

  /**
   * Update product plan discount for an establishment (sales view)
   * @param establishmentId
   * @param productPlanId
   * @param data
   * @returns
   */
  updateEstablishmentProductPlanDiscount(
    establishmentId: number,
    productPlanId: number,
    data: {
      discount_type: "fixed" | "percentage"
      discount_percent?: number | null
      discount_eur?: number | null
      discount_chf?: number | null
      discount_cad?: number | null
    }
  ): Promise<any> {
    return http.put(`auth/establishments/${establishmentId}/product-plans/${productPlanId}/discount`, data)
  }

  /**
   * Create new establishment
   * @param establishment
   * @param headers
   * @returns
   */
  createEstablishment(establishment: ICreateEstablishment, headers: {}): Promise<any> {
    return http.post(`auth/establishments/create`, establishment, {
      headers: headers,
    })
  }

  checkIfEstablishmentExists(ids: Number[], headers: {}): Promise<any> {
    return http.post(
      `auth/establishments/find-duplication`,
      {
        provider_ids: ids,
      },
      {
        headers: headers,
      }
    )
  }

  /**
   * Set the establishment is_updatable property
   * Define if the establishment is ready to be sync (enable/disable the sync button)
   * @param establishmentId
   * @param updatable
   * @param headers
   * @returns
   */
  updateEstablishmentUpdatable(establishmentId: number, updatable: boolean, headers: {}): Promise<void> {
    return http.post(
      `auth/establishments/updatable-status/update`,
      {
        establishment_id: establishmentId,
        is_updatable: updatable,
      },
      {
        headers: headers,
      }
    )
  }

  /**
   * Update the general and base
   * @param establishment
   * @param headers
   * @returns
   */
  updateEstablishment(establishment: IEstablishmentGeneral, headers: {}): Promise<any> {
    return http.post(`auth/establishments/update`, establishment, {
      headers: headers,
    })
  }

  /**
   * Updates establishment's hours
   * @returns
   * @param payload
   */
  updateEstablishmentHours(payload: IEstablishmentHours): Promise<void> {
    return http.post(`/auth/establishments/opening-hours/update`, payload)
  }

  deleteEstablishmentPlannedPeriod(periodId: number): Promise<void> {
    return http.delete(`/auth/establishments/opening-period/delete/${periodId}`)
  }

  /**
   * Updates establishment's contact tab
   * @returns
   * @param payload
   */
  updateContact(payload: IEstablishmentContact): Promise<void> {
    return http.post(`/auth/establishments/communications/update`, payload)
  }

  /**
   * Updates establishment's specific tab
   * @returns
   * @param payload
   */
  updateSpecific(payload: IEstablishmentSpecific): Promise<void> {
    return http.post(`/auth/establishments/specific/update`, payload)
  }

  /**
   * Set the first plateforme connexion success date
   * @param establishmentId
   * @returns
   */
  setFirstConnexionDate(establishmentId: number): Promise<void> {
    return http.get(`/auth/establishments/set-first-connection/${establishmentId}`)
  }

  initWithGoogleData(establishmentId: number): Promise<void> {
    return http.get(`/auth/establishments/google-init/${establishmentId}`)
  }

  /**
   * Get the establishment
   * @param id
   * @returns
   * @param payload
   */
  getEstablishmentById(id: number, headers: {}): Promise<void> {
    return http.get(`/auth/establishments/${id}`, { headers: headers })
  }

  /**
   * Get the attributes for the establishment's category
   * @param establishmentId
   * @returns
   */
  getAttributes(establishmentId: number): Promise<void> {
    return http.get(`/auth/establishments/attributes/${establishmentId}`)
  }

  /**
   * Get the dashboard welcome checklist status
   * @param establishmentId
   * @returns
   */
  getChecklist(establishmentId: number): Promise<void> {
    return http.get(`/auth/users/status/${establishmentId}`)
  }

  updateLnInscriptionChecklist(): Promise<void> {
    return http.get(`/auth/establishments/set-subscription-ln`)
  }

  /**
   * Update the viewsSolicitation property (user viewed for the first time their solicitations)
   * @param establishmentId
   * @returns
   */
  updateViewedSolicitations(establishmentId: number): Promise<void> {
    return http.get(`/auth/users/seen-solicitations/${establishmentId}`)
  }

  /**
   * Get the admin user of an establishment
   * @param establishmentId
   * @param headers
   * @returns
   */
  getEstablishmentAdminUser(establishmentId: number, headers: {}): Promise<any> {
    return http.get(`/auth/establishments/${establishmentId}/admin-user`, { headers: headers })
  }

  /**
   * Get the sale user (commercial) of an establishment
   * @param establishmentId
   * @returns
   */
  getEstablishmentSaleUser(establishmentId: number): Promise<any> {
    return http.get(`/auth/establishments/${establishmentId}/sale-user`)
  }

  /**
   * Calculate cart totals (HT, discount, TTC) from backend.
   * Used when selecting/deselecting offers on checkout.
   */
  async calculateCart(
    establishmentId: number,
    payload: {
      items: Array<{
        product_plan_code: string
        quantity?: number
        shooting_date?: string | null
        selected_options?: number[]
      }>
      billing_country?: string | null
    }
  ): Promise<{
    total_ht_cents: number
    discount_cents: number | null
    total_ttc_cents: number
    lines: Array<{ description: string; amount_cents: number }>
  }> {
    const res = await http.post<{ success: boolean; data: { total_ht_cents: number; discount_cents: number | null; total_ttc_cents: number; lines: Array<{ description: string; amount_cents: number }> } }>(
      `auth/establishments/${establishmentId}/cart/calculate`,
      {
        items: payload.items,
        billing_country: payload.billing_country ?? "FR",
      }
    )
    return res.data?.data ?? { total_ht_cents: 0, discount_cents: null, total_ttc_cents: 0, lines: [] }
  }

  async calculateUpsellCart(
    establishmentId: number,
    contractId: number,
    payload: {
      items: Array<{
        product_plan_code: string
        quantity?: number
        shooting_date?: string | null
        selected_options?: number[]
      }>
      billing_country?: string | null
    }
  ): Promise<{
    total_ht_cents: number
    discount_cents: number | null
    total_tax_cents: number
    total_ttc_cents: number
    lines: Array<{ description: string; amount_cents: number }>
    upsell?: {
      contract_id: number
      current_product_plan_id: number
      current_product_plan_code: string
      current_contract_name?: string
      current_contract_total_ht_cents?: number
      current_contract_valid_from?: string
      current_contract_valid_through?: string
      prorata_total_days?: number
      prorata_remaining_days?: number
      prorata_percentage?: number
      prorata_refund_from?: string
      prorata_refund_through?: string
      prorata_discount_cents: number
      old_contract_valid_through: string
      new_contract_valid_from: string
    }
  }> {
    const res = await http.post(`auth/establishments/${establishmentId}/contracts/${contractId}/upsell/calculate`, {
      items: payload.items,
      billing_country: payload.billing_country ?? "FR",
    })
    return res.data?.data ?? { total_ht_cents: 0, discount_cents: null, total_tax_cents: 0, total_ttc_cents: 0, lines: [] }
  }
}

export default new EstablishmentDataService()
